import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Linkedin, Youtube, Instagram, Github, Mail, MapPin, Phone, Globe } from 'lucide-react';
import { HOME } from '@/constants/testIds';
import { useSite } from '@/context/SiteContext';

const DEFAULT_LOGO_URL = '/mgs-logo.png';

/* ── Custom SVGs (not in Lucide) ── */
function DiscordIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

function ThreadsIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.858-6.43 2.523-8.482C5.852 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.74-1.762-.438-.517-1.089-.792-1.998-.830-1.441-.06-2.503.537-3.159 1.775l-1.818-1.022C7.968 3.97 9.754 3.03 12.18 3.03c2.41.1 4.043.884 4.853 2.33.63 1.108.92 2.6.862 4.434.047.013.095.026.142.04 1.204.347 2.158 1.008 2.763 1.913.757 1.14 1.014 2.62.72 4.168-.433 2.28-1.79 4.101-3.826 5.14-1.535.786-3.373 1.163-5.508 1.163z" />
    </svg>
  );
}

/* ── Platform accent colours for hover glow ── */
const PLATFORM_COLORS = {
  facebook:  '#1877F2',
  twitter:   '#1DA1F2',
  x:         '#000000',
  linkedin:  '#0A66C2',
  youtube:   '#FF0000',
  instagram: '#E1306C',
  discord:   '#5865F2',
  whatsapp:  '#25D366',
  github:    '#333333',
  threads:   '#000000',
};

/* ── Social platform icon map ── */
const SOCIAL_ICON_MAP = {
  facebook:  Facebook,
  twitter:   Twitter,
  x:         Twitter,
  linkedin:  Linkedin,
  youtube:   Youtube,
  instagram: Instagram,
  discord:   DiscordIcon,
  whatsapp:  WhatsAppIcon,
  github:    Github,
  threads:   ThreadsIcon,
};

/* ── MGS brand tagline ── */
const BRAND_TAGLINE =
  'Two decades of engineering excellence — building AI-first digital products that drive real growth for businesses across 40+ countries. Quality is not our promise; it is our culture.';

const DEFAULT_COLS = [
  {
    title: 'Our Services',
    items: [
      { label: 'Generative AI & Chatbots',              to: '/services/generative-ai-chatbots' },
      { label: 'Machine Learning & Analytics',           to: '/services/machine-learning' },
      { label: 'NLP & Computer Vision',                  to: '/services/nlp-computer-vision' },
      { label: 'RAG Systems',                            to: '/services/rag-systems' },
      { label: 'AI Agents',                              to: '/services/ai-agents' },
      { label: 'Workflow Automation',                    to: '/services/workflow-automation' },
      { label: 'AI Integration & Consulting',            to: '/services/ai-integration-consulting' },
      { label: 'Web & Mobile Development',               to: '/services/web-mobile-development' },
      { label: 'ERP Solutions',                          to: '/services/erp-solutions' },
      { label: 'UI/UX Design',                           to: '/services/ui-ux-design' },
    ],
  },
  {
    title: "Let's Explore",
    items: [
      { label: 'About / Why MGS',  to: '/why-mgs'              },
      { label: 'Our Team',         to: '/our-team'              },
      { label: 'Case Study',       to: '/case-studies'          },
      { label: 'Career',           to: '/career'                },
      { label: 'Client Speaks',     to: '/reviews'               },
      { label: 'Contact Us',       to: '/contact'               },
      { label: 'Request a Quote',  to: '/contact?type=quote'    },
      { label: 'Partner With Us',  to: '/contact?type=partner'  },
    ],
  },
  {
    title: 'Legal',
    items: [
      { label: 'Content Guidelines', to: '/p/content-guidelines' },
      { label: 'Legal Disclosure',   to: '/p/legal-disclosure'   },
      { label: 'Privacy Policy',     to: '/p/privacy-policy'     },
      { label: 'Security',           to: '/p/security'           },
      { label: 'Terms of Service',   to: '/p/terms-of-service'   },
    ],
  },
];

const DEFAULT_CONTACT = {
  address: [
    'Samartha Apartment, #202, Door No. 2405,',
    '18th Main Road, Kumaraswamy Layout 2nd Stage,',
    'Bengaluru – 560078, Karnataka, India',
  ],
  phone:   ['+91-7795287075', '+91-80-49746380', '+91-80-40935246'],
  email:   ['ceo@maxglobalsoft.com', 'info@maxglobalsoft.com'],
  website: 'https://www.maxglobalsoft.com',
};

const DEFAULT_COPYRIGHT =
  '© 2026 Max Global Software. All rights reserved. — Quality Beyond Desires';

/* Default socials — admin can override via CMS */
const DEFAULT_SOCIALS = [
  { platform: 'facebook',  url: '#' },
  { platform: 'linkedin',  url: '#' },
  { platform: 'twitter',   url: '#' },
  { platform: 'instagram', url: '#' },
  { platform: 'youtube',   url: '#' },
  { platform: 'discord',   url: '#' },
];

/* ── Framer Motion variants ── */
const socialsContainer = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.07, delayChildren: 0.2 },
  },
};

const socialItem = {
  hidden: { opacity: 0, y: 12, scale: 0.8 },
  show:   { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

export default function Footer() {
  const { data: site } = useSite();

  const LOGO_URL  = site?.logo_url || DEFAULT_LOGO_URL;
  const rawCols   = Array.isArray(site?.footer_columns) && site.footer_columns.length
    ? site.footer_columns
    : DEFAULT_COLS;

  const cols = rawCols.map((col) => ({
    title: col.title || col.heading || '',
    items: (col.items || col.links || []).map((it) => ({
      label: it.label || '',
      to:    it.to || it.href || '#',
    })),
  }));

  const contact      = { ...DEFAULT_CONTACT, ...(site?.contact || {}) };
  const addressLines = Array.isArray(contact.address)
    ? contact.address
    : typeof contact.address === 'string'
    ? [contact.address]
    : DEFAULT_CONTACT.address;
  const phones   = Array.isArray(contact.phone) ? contact.phone : contact.phone ? [contact.phone] : DEFAULT_CONTACT.phone;
  const emails   = Array.isArray(contact.email) ? contact.email : contact.email ? [contact.email] : DEFAULT_CONTACT.email;
  const websiteUrl = contact.website || DEFAULT_CONTACT.website;

  const socials  = Array.isArray(site?.socials) && site.socials.length
    ? site.socials
    : DEFAULT_SOCIALS;

  const copyright   = site?.copyright   || DEFAULT_COPYRIGHT;
  const foundedYear = site?.founded_year || '2005';
  const ledBy       = site?.led_by       || 'Bhupendra Verma';

  return (
    <footer
      id="footer"
      data-testid={HOME.footer}
      className="relative bg-white text-slatebody pt-16 pb-8 border-t border-hairline"
    >
      {/* Gold top hairline */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E8C25A]/40 to-transparent" />

      {/* ── Top row: Brand + link columns ── */}
      <div className="container mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 xl:gap-10">

        {/* Brand column — logo, tagline, socials */}
        <div className="md:col-span-3">
          <img
            src={LOGO_URL}
            alt="Max Global Software"
            className="h-44 w-auto object-contain -mb-2"
          />
          <p className="mb-5 text-[13px] leading-relaxed text-slatebody/80">
            {BRAND_TAGLINE}
          </p>

          {/* ── Animated social icons ── */}
          <motion.div
            key={site ? 'api' : 'loading'}
            className="flex flex-wrap items-center gap-2"
            variants={socialsContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            {socials.map((s, idx) => {
              const key    = (s.platform || '').toLowerCase();
              const Icon   = SOCIAL_ICON_MAP[key] || Globe;
              const hasUrl = s.url && s.url !== '#';
              const color  = PLATFORM_COLORS[key] || '#2F6FB0';
              return (
                <motion.a
                  key={`${key}-${idx}`}
                  href={s.url || '#'}
                  aria-label={s.platform || 'social link'}
                  title={s.platform}
                  target={hasUrl ? '_blank' : undefined}
                  rel={hasUrl ? 'noopener noreferrer' : undefined}
                  variants={socialItem}
                  whileHover={{
                    scale: 1.18,
                    y: -3,
                    backgroundColor: color,
                    borderColor: color,
                    color: '#ffffff',
                    boxShadow: `0 6px 20px ${color}55`,
                    transition: { duration: 0.22, ease: 'easeOut' },
                  }}
                  whileTap={{ scale: 0.92 }}
                  className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-hairline text-navy-deep/60 cursor-pointer"
                  style={{ color: 'inherit' }}
                >
                  <Icon className="h-4 w-4" />
                </motion.a>
              );
            })}
          </motion.div>
        </div>

        {/* ── Link columns ── */}
        {cols.map((col, colIdx) => (
          <div key={col.title || colIdx} className="md:col-span-2">
            <h4 className="text-navy-deep font-semibold text-sm mb-4">{col.title}</h4>
            <ul className="space-y-2">
              {(col.items || []).map((it) => {
                const isInternal = it.to && it.to.startsWith('/');
                const content = (
                  <span className="text-[13px] text-slatebody hover:text-[#C9962E] transition-colors leading-snug">
                    {it.label}
                  </span>
                );
                return (
                  <li key={it.label}>
                    {isInternal ? <Link to={it.to}>{content}</Link> : <a href={it.to || '#'}>{content}</a>}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* ── Find Us ── */}
        <div className="md:col-span-3">
          <h4 className="text-navy-deep font-semibold text-sm mb-4">Find Us</h4>
          <ul className="space-y-4">
            <li className="flex gap-2 items-start text-slatebody text-[13px]">
              <MapPin className="h-4 w-4 mt-0.5 text-brandblue shrink-0" />
              <span className="leading-relaxed">
                {addressLines.map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}{idx < addressLines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </span>
            </li>
            <li className="flex gap-2 items-start text-slatebody text-[13px]">
              <Phone className="h-4 w-4 mt-0.5 text-brandblue shrink-0" />
              <span className="leading-relaxed">
                {phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, '')}`} className="hover:text-[#C9962E] transition-colors block">{p}</a>
                ))}
              </span>
            </li>
            <li className="flex gap-2 items-start text-slatebody text-[13px]">
              <Mail className="h-4 w-4 mt-0.5 text-brandblue shrink-0" />
              <span className="leading-relaxed">
                {emails.map((e) => (
                  <a key={e} href={`mailto:${e}`} className="hover:text-[#C9962E] transition-colors block">{e}</a>
                ))}
              </span>
            </li>
            <li className="flex gap-2 items-center text-slatebody text-[13px]">
              <Globe className="h-4 w-4 text-brandblue shrink-0" />
              <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[#C9962E] transition-colors">
                {websiteUrl.replace(/^https?:\/\//, '')}
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="container mx-auto mt-12 pt-6 border-t border-hairline flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-xs text-slatebody">{copyright}</p>
        <p className="text-xs text-slatebody">Founded {foundedYear} · Led by {ledBy}</p>
      </div>
    </footer>
  );
}
